import { doc, getDoc } from "firebase/firestore";
import { ArrowLeft, Megaphone } from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import BroadcastComposer from "src/components/broadcasts/BroadcastComposer";
import NotificationPreview from "src/components/broadcasts/NotificationPreview";
import { actorFromAuth } from "src/lib/activityService";
import { APP_ENV } from "src/lib/appEnv";
import {
  buildDataPayload,
  callSendBroadcast,
  deleteBroadcastDraft,
  saveBroadcastDraft,
  validateForm,
} from "src/lib/broadcastService";
import { firestore } from "src/lib/firebase_config";
import { canManageService, canReadService, ServiceDomain } from "src/lib/roles";
import { selectUser, selectUserProfile } from "src/store/authSlice";
import { useOperatorAuthStore } from "src/store/operatorAuthStore";
import type {
  BroadcastFormState,
  NotificationHistoryDoc,
  NotificationHistoryListItem,
} from "src/types/broadcasts";
import { Constants } from "../constant";

const INITIAL_FORM: BroadcastFormState = {
  env: APP_ENV,
  type: "NEWS",
  title: "",
  body: "",
  tapKind: "none",
  navTarget: "",
  navEvent: "",
  navParamsText: "",
  url: "",
};

interface ComposeLocationState {
  prefill?: Partial<BroadcastFormState>;
  /**
   * When set, the composer is editing an existing draft row. Save-as-draft
   * updates the same doc; Send deletes it after the publish callable succeeds.
   */
  draftId?: string;
}

const BroadcastsComposePanel: React.FC = () => {
  const user = useSelector(selectUser);
  const userProfile = useSelector(selectUserProfile);
  const authToken = useOperatorAuthStore((s) => s.token);
  const tokenEnv = useOperatorAuthStore((s) => s.tokenEnv);
  const clearAuthToken = useOperatorAuthStore((s) => s.clearToken);
  const navigate = useNavigate();
  const location = useLocation();
  const navState = location.state as ComposeLocationState | null;
  const prefill = navState?.prefill;
  const [draftId, setDraftId] = useState<string | null>(
    navState?.draftId ?? null,
  );

  useEffect(() => {
    if (!user) clearAuthToken();
  }, [user, clearAuthToken]);

  const canManage =
    userProfile != null &&
    canManageService(userProfile.role, ServiceDomain.Broadcasts);

  const [form, setForm] = useState<BroadcastFormState>(() => ({
    ...INITIAL_FORM,
    ...(prefill ?? {}),
    // Env is fixed to this deployment — never inherit it from a prefill/draft.
    env: APP_ENV,
  }));
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const [sendSuccess, setSendSuccess] = useState<string | null>(null);
  const [savingDraft, setSavingDraft] = useState(false);
  const [draftError, setDraftError] = useState<string | null>(null);
  const [draftSuccess, setDraftSuccess] = useState<string | null>(null);

  // When a draftId is in route state but the prefill came in empty (e.g. a
  // refresh on /broadcasts/create), pull the doc so the operator sees their
  // saved values. Skipped when prefill is present (Duplicate / Edit both pass
  // it from the list).
  useEffect(() => {
    if (!draftId || prefill) return;
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDoc(
          doc(firestore, Constants.NOTIFICATION_HISTORY_COLLECTION, draftId),
        );
        if (cancelled || !snap.exists()) return;
        const data = snap.data() as NotificationHistoryDoc;
        if (data.status !== "draft") return;
        const item: NotificationHistoryListItem = { id: snap.id, ...data };
        // Lazy-import to avoid a circular import with broadcastService.
        const { historyItemToFormPrefill } =
          await import("src/lib/broadcastService");
        setForm((cur) => ({
          ...cur,
          ...historyItemToFormPrefill(item),
          env: APP_ENV,
        }));
      } catch (e) {
        console.error("Failed to load draft:", e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [draftId, prefill]);

  const formError = useMemo(() => {
    if (!form.title && !form.body) return null;
    return validateForm(form, authToken, tokenEnv);
  }, [form, authToken, tokenEnv]);

  const handleSend = async () => {
    setSendError(null);
    setSendSuccess(null);
    setDraftError(null);
    setDraftSuccess(null);
    const localError = validateForm(form, authToken, tokenEnv);
    if (localError) {
      setSendError(localError);
      return;
    }
    setSending(true);
    try {
      const dataPayload = buildDataPayload(form);
      await callSendBroadcast(
        {
          env: form.env,
          token: authToken,
          type: form.type,
          title: form.title.trim(),
          body: form.body.trim(),
          dataPayload,
        },
        actorFromAuth(user, userProfile),
      );
      // If we were editing a draft, delete it now that a published row exists
      // for the same content. The send activity entry was already written by
      // the callable, so the draft delete logs separately for traceability.
      if (draftId) {
        const actor = actorFromAuth(user, userProfile);
        const stubItem = {
          id: draftId,
          env: form.env,
          type: form.type,
          title: form.title,
        } as unknown as NotificationHistoryListItem;
        try {
          await deleteBroadcastDraft(stubItem, actor);
        } catch (e) {
          console.error("Failed to delete draft after send:", e);
        }
        setDraftId(null);
      }
      setSendSuccess(
        `Nice — your ${form.type === "NEWS" ? "News" : "Promo"} broadcast went out to ${form.env}. Redirecting…`,
      );
      window.setTimeout(() => navigate(Constants.BROADCASTS_ROUTE), 900);
    } catch (e) {
      setSendError(e instanceof Error ? e.message : "Send failed.");
    } finally {
      setSending(false);
    }
  };

  const handleSaveDraft = async () => {
    setSendError(null);
    setSendSuccess(null);
    setDraftError(null);
    setDraftSuccess(null);
    // Drafts only need *some* content — refuse to write a completely empty
    // doc so we don't pollute the list. Everything else (token, payload
    // shape) is deferred to Send.
    if (!form.title.trim() && !form.body.trim()) {
      setDraftError("Add a title or body before saving a draft.");
      return;
    }
    const actor = actorFromAuth(user, userProfile);
    if (!actor) {
      setDraftError("Sign in again to save drafts.");
      return;
    }
    setSavingDraft(true);
    try {
      const id = await saveBroadcastDraft(form, actor, draftId ?? undefined);
      setDraftId(id);
      setDraftSuccess(
        draftId
          ? "Draft updated."
          : "Saved as draft. You can edit it from the broadcasts list.",
      );
      window.setTimeout(() => navigate(Constants.BROADCASTS_ROUTE), 700);
    } catch (e) {
      setDraftError(e instanceof Error ? e.message : "Could not save draft.");
    } finally {
      setSavingDraft(false);
    }
  };

  if (
    !userProfile ||
    !canReadService(userProfile.role, ServiceDomain.Broadcasts)
  ) {
    return (
      <div className="py-6 sm:py-8">
        <h1 className="mb-3 text-2xl font-bold sm:mb-4 sm:text-4xl">
          Access Denied
        </h1>
        <p className="text-sm text-gray-400 sm:text-base">
          You don&apos;t have permission to view broadcasts.
        </p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-6xl py-2 sm:py-4">
      <header className="mb-6 sm:mb-8">
        <button
          type="button"
          onClick={() => navigate(Constants.BROADCASTS_ROUTE)}
          className="mb-3 inline-flex items-center gap-1.5 rounded-lg px-2 py-1 text-xs font-extrabold uppercase tracking-wide text-[#a9acb3] hover:text-[#ffffff]"
        >
          <ArrowLeft className="h-3.5 w-3.5" aria-hidden /> Back to broadcasts
        </button>
        <h1 className="mb-2 flex items-center gap-2 text-2xl font-bold sm:gap-3 sm:text-4xl">
          <Megaphone className="h-7 w-7 shrink-0 text-white sm:h-8 sm:w-8" />
          {draftId
            ? "Edit draft"
            : prefill
              ? "Duplicate broadcast"
              : "New broadcast"}
        </h1>
        <p className="max-w-2xl text-sm text-[#d7dadf] sm:text-base">
          {Constants.BROADCASTS_DESCRIPTION}
        </p>
      </header>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1.4fr_1fr] lg:items-start">
        <BroadcastComposer
          form={form}
          onChange={setForm}
          onSend={handleSend}
          onSaveDraft={handleSaveDraft}
          sending={sending}
          savingDraft={savingDraft}
          editingDraft={Boolean(draftId)}
          canManage={canManage}
          formError={formError}
          sendError={sendError}
          sendSuccess={sendSuccess}
          draftError={draftError}
          draftSuccess={draftSuccess}
        />
        <NotificationPreview form={form} />
      </div>
    </div>
  );
};

export default BroadcastsComposePanel;
